/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Global Error Boundary Component
 * Catches JavaScript errors anywhere in the child component tree
 * and displays a fallback UI instead of crashing the whole app.
 */

import React, { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AlertTriangle, RefreshCw, Home, Mail } from "lucide-react";
import { logger } from "@/lib/logger";
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from "@/lib/constants";
import { buildDashboardPath } from "@/hooks/use-lang-prefix";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // SU-7 (FCR S323): Detect Vite chunk-load errors caused by stale browser cache
    // after a deploy. "Refresh once and it works" is the signature — the cached
    // index.html references old chunk hashes that no longer exist on the CDN.
    // A single silent reload clears the stale reference. sessionStorage prevents
    // an infinite reload loop if the error persists after the fresh load.
    const isChunkError =
      /Failed to fetch dynamically imported module/i.test(error.message) ||
      error.name === "ChunkLoadError";

    if (isChunkError && !sessionStorage.getItem("chunk_reloaded")) {
      sessionStorage.setItem("chunk_reloaded", "1");
      window.location.reload();
      return;
    }

    // Log error to Sentry
    Sentry.withScope((scope: Sentry.Scope) => {
      scope.setExtras({
        componentStack: errorInfo.componentStack,
      });
      Sentry.captureException(error);
    });

    // Update state with error info
    this.setState({
      error,
      errorInfo,
    });

    // Log to console in development using centralized logger
    // Logger already handles DEV check internally, but keep explicit check for clarity
    if (import.meta.env.DEV) {
      logger.error("Error caught by ErrorBoundary:", error);
      logger.error("Component stack:", errorInfo.componentStack);
    }
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleGoHome = (): void => {
    // Extract the active language from the URL path (e.g. /fr/dashboard/... → "fr").
    // Falls back to "en" when the path has no recognizable lang segment.
    const langMatch = window.location.pathname.match(/^\/([a-z]{2})(?:\/|$)/);
    const lang = langMatch ? langMatch[1] : "en";
    window.location.href = buildDashboardPath(lang, "overview");
  };

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <Card className="w-full max-w-lg">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900">
                Something went wrong
              </CardTitle>
              <CardDescription className="text-gray-600">
                We apologize for the inconvenience. An unexpected error has
                occurred.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {import.meta.env.DEV && this.state.error && (
                <div className="rounded-lg bg-gray-100 p-4">
                  <p className="text-sm font-mono text-red-600 break-all">
                    {this.state.error.message}
                  </p>
                  {this.state.errorInfo?.componentStack && (
                    <pre className="mt-2 text-xs text-gray-500 overflow-auto max-h-40">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              )}

              <div className="flex flex-col gap-3">
                <Button
                  onClick={this.handleReload}
                  className="w-full"
                  variant="default"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Refresh Page
                </Button>

                <Button
                  onClick={this.handleGoHome}
                  className="w-full"
                  variant="outline"
                >
                  <Home className="mr-2 h-4 w-4" />
                  Go to Dashboard
                </Button>

                <Button
                  onClick={this.handleReset}
                  className="w-full"
                  variant="ghost"
                >
                  Try Again
                </Button>
              </div>

              <div className="pt-4 border-t text-center">
                <p className="text-sm text-gray-500">
                  If this problem persists, please contact our support team.
                </p>
                <a
                  href={SUPPORT_MAILTO}
                  className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 mt-2"
                >
                  <Mail className="h-4 w-4" />{SUPPORT_EMAIL}</a>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
